<script setup>
import {onMounted, ref} from "vue";
import router from "../../router.js";
import {checkForBankIdLogin, generate2faCode, getBankIdUrl, initialize, login} from '@/services/AuthService';
import {getTenantData} from '@/services/TenantService';
import {Capacitor} from '@capacitor/core';
import Footer from '@/components/layouts/Footer.vue';
import {authenticateWithBiometric, getAvailableTypeString} from '@/services/BiometricService';
import {BiometricAuth} from '@aparajita/capacitor-biometric-auth';
import {Browser} from '@capacitor/browser';
import AppAnnouncements from '@/components/AppAnnouncements.vue';
import {setButtonContentDefault, setButtonContentLoading, allowedBankID, getApiBaseUrl, allowedBiometricAuth, maskPhoneNumber} from '@/helper';
import {useI18n} from 'vue-i18n';
import {useRoute} from 'vue-router';
import axios from 'axios';
import {appVersion} from "@/services/AppService";

const queryParams = useRoute().query;
const {t} = useI18n()
const email = ref('');
const password = ref('');
const phone = ref('');
const theme = ref({});
const errorMessage = ref('');
const step = ref(1);
const biometricInfo = ref(null);
const biometricTypeAvailable = ref('');
const twoFactorCode = ref(null);
const codeSent = ref(false);
const codeSentAgain = ref(false);
const codeRequestInterval = ref(0);
const userId = ref(null);
const msftLoginUrl = ref(getApiBaseUrl() + '/auth/azure/redirect?provider=azure');
const showMsftLogin = ref(false);
const showBiometricLogin = ref(false);
const maintenanceMode = ref(localStorage.getItem('MAINTENANCE_MODE') === 'true');

// when domain select overlay is closed, check for biometric login again
emitter.on('domainOverlayClosed', () => {
  initializeLogin();
});

onMounted(() => {
  const urlParams = new URLSearchParams(window.location.search);
  const state = urlParams.get('state');
  const code = urlParams.get('code');

  if (Capacitor.isNativePlatform()) {
    msftLoginUrl.value += '&capacitor=true';
  }

  if (state && code) {
    setButtonContentLoading('btn-bankid-login');
  }

  getTenantData()
      .then((response) => {
        theme.value = response.theme;
      });
  initializeLogin();

  setInterval(() => {
    maintenanceMode.value = localStorage.getItem('MAINTENANCE_MODE') === 'true';
  }, 30000);

});

const initializeLogin = async () => {
  const lsValue = localStorage.getItem('IE_DOMAIN');
  checkForBankIdLogin();
  checkForTokenLogin();
  errorMessage.value = null;
  biometricInfo.value = await BiometricAuth.checkBiometry();
  biometricTypeAvailable.value = await getAvailableTypeString()
  if (await allowedBiometricAuth()) {
    showBiometricLogin.value = true;
  }
  if (await allowedBiometricAuth() && !window.location.href.includes('biometric=false') && (lsValue === 'SK' || lsValue === 'CZ')) {
    await authenticateWithBiometric();
  } else {
    step.value = 1;
  }
}

const checkForTokenLogin = () => {
  if (!window.location.href.includes('capacitor=true')) {
    const token = queryParams.token;

    if (token) {
      localStorage.setItem('ACCESS_TOKEN', token);
      localStorage.setItem('EXPIRY', Math.floor(Date.now() / 1000) + (process.env.VUE_APP_JWT_TTL * 60));
      if (queryParams.client_id) {
        router.push({name: 'ClientDetail', params: {id: queryParams.client_id}});
      }
      window.location.href = '/home';
    }
  }
}

const submit = () => {
  setButtonContentLoading('btn-login');
  errorMessage.value = null;

  localStorage.removeItem('TENANT_THEME');
  getTenantData(true, email.value)
      .then((response) => {
        theme.value = response.theme;
      });

  login(email.value, step.value >= 1 ? password.value : null, null, twoFactorCode.value).then(response => {
    if (response.access_token) {
      router.push('/home');
    }
    if (response.message === 'user_has_password') {
      step.value = 2;
    }
    if (response.message === 'verification_code.generated') {
      phone.value = response.phone;
      userId.value = response.user_id;
      step.value = 3;
      codeRequestInterval.value = 30;
      codeSent.value = true;
      startStopwatch();
    }
    if (response.message === 'user_has_no_password') {
      router.push(`/activation?email=${encodeURIComponent(email.value)}&phone=${encodeURIComponent(response.phone)}&user_id=${response.user_id}`);
    }
    if (response.auth_providers?.includes('AZURE')) {
      showMsftLogin.value = true;
    }
    setButtonContentDefault('btn-login');
  }, (error) => {
    errorMessage.value = error.message;
    if (error.message === 'wrong_credentials') {
      errorMessage.value = t('login.wrong_credentials');
    }
    if (error.message === 'verification_code.INVALID_CODE') {
      errorMessage.value = t('login.invalid_auth_code');
    }
    // vyčerpán počet pokusů o zadání kódu - BE kód dočasně vůbec neověřuje
    if (error.message === 'verification_code.TOO_MANY_ATTEMPTS') {
      errorMessage.value = t('verification_code.TOO_MANY_ATTEMPTS');
    }
    if (error.message === 'global.not_found') {
      errorMessage.value = t('login.user_not_found');
    }
    setButtonContentDefault('btn-login');
  });
}

const authenticateWithBankID = () => {
  setButtonContentLoading('btn-bankid-login');
  getBankIdUrl()
      .then(response => {
        const state = new URL(response.url).searchParams.get('state');
        localStorage.setItem('PROVIDER_STATE_ID', state);
        if (Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('Browser')) {
          void Browser.open({url: response.url});
        } else {
          window.location.href = response.url;
        }
      });
}

const loginViaProvider = (url) => {
  if (Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('Browser')) {
    void Browser.open({url});
  } else {
    window.location.href = url;
  }
}

const generateCode = () => {
  codeSentAgain.value = true;
  errorMessage.value = null;
  const form = {
    verifiable_type: 'USER',
    verifiable_id: userId.value,
    service: 'SMS',
    recipient: phone.value,
  }
  setButtonContentLoading('btn-activate');

  generate2faCode(form).then(response => {
    codeRequestInterval.value = 30;
    codeSent.value = true;
    setButtonContentDefault('btn-activate');
    startStopwatch();
  }, (error) => {
    errorMessage.value = error.message;
    setButtonContentDefault('btn-activate');
    codeSentAgain.value = false;
  });
}


const startStopwatch = () => {
  const interval = setInterval(() => {
    codeRequestInterval.value--;
    if (codeRequestInterval.value <= 0) {
      clearInterval(interval);
      codeSentAgain.value = false;
    }
  }, 1000);
}

const resetForm = () => {
  step.value = 1;
  errorMessage.value = null;
  twoFactorCode.value = null;
  password.value = null;
}

</script>

<template>
  <div class="entrance">
    <header class="entrance__header" v-if="Capacitor.isNativePlatform()"></header>
    <div class="min-vh-device row justify-content-center align-items-center">
      <div class="col-12 col-md-8 col-lg-6 col-xl-4">
        <div class="entrance__logo" v-if="theme && theme.logoBase64">
          <img :src="theme.logoBase64" v-if="theme && theme.logoBase64" alt=""/>
        </div>
        <div v-if="!maintenanceMode" class="entrance__title">
          {{ t('login.login') }}
        </div>
        <div v-else class="d-flex justify-content-center">
          <span class="mdi mdi-cog-sync mdi mdi-large-icon"></span>
        </div>
        <div v-if="maintenanceMode" class="text-center mt-3">
          <div class="maintenance-title">{{ t('login.maintenance_title') }}</div>
          <div class="maintenance-description mt-3">{{ t('login.maintenance_description') }}</div>
        </div>
        <div v-if="!maintenanceMode" class="entrance__content">
          <!-- IMA-519: časově omezená oznámení (např. odstávka Bank iD) nad přihlašovacím formulářem -->
          <AppAnnouncements/>
          <div class="card">
            <div class="card-body">
              <form @submit.prevent="submit">

                <div v-if="step !== 3" class="form-group">
                  <label class="form-label" for="email">{{ t('login.email:') }}</label>
                  <input id="email" class="form-control"
                         type="email" placeholder="" autocomplete="off"
                         v-model="email" required/>
                </div>
                <div v-if="step === 2" class="form-group">
                  <label class="form-label" for="password">{{ t('login.password:') }}</label>
                  <input id="password" class="form-control"
                         type="password" placeholder="" autocomplete="off"
                         v-model="password" required/>
                </div>
                <div v-if="step === 3" class="form-group">
                  <div class="text-center mb-3">{{ t('login.for_login_enter_auth_code') }}</div>
                  <label class="form-label" for="2fa_code">{{ t('login.phone_number2') }}</label>
                  <input id="2fa_code" class="form-control"
                         type="text" placeholder="" autocomplete="off" readonly disabled
                         :value="maskPhoneNumber(phone)" required/>
                </div>
                <div v-if="step === 3" class="form-group">
                  <div class="form-label d-flex justify-content-between">
                    <label for="2fa_code">{{ t('login.auth_code') }}</label>
                  </div>
                  <input id="2fa_code" class="form-control"
                         type="text" placeholder="" autocomplete="off"
                         v-model="twoFactorCode" required/>
                  <div class="mt-2">
                    <a :class="codeSentAgain === true ? 'pointer-events-none' : ''" class="cursor-pointer" @click="generateCode" v-if="codeRequestInterval === 0"><span>{{ t('login.send_code') }} </span> <span v-if="codeSent"> {{ t('login.again') }} </span> </a>
                    <span class="text-muted" v-if="codeRequestInterval > 0">{{ t('login.auth_code_sent') }} {{ codeRequestInterval }}{{ t('login.secs') }}</span>
                  </div>
                </div>

                <div class="form-group">
                  <!-- Submit Button -->
                  <button id="btn-login" type="submit" class="btn btn-primary w-100">
                    <span>{{ t('login.log_in') }}</span>
                  </button>
                  <div class="alert alert-danger mt-3" v-if="errorMessage"> {{ errorMessage }}</div>
                  <div v-if="showBiometricLogin && step !== 3" class="my-3">
                    <button id="btn-biometric-login" type="button" @click="authenticateWithBiometric()" class="btn btn--with-icon btn--face-id">
                      <span class="btn-icon"></span>
                      <span class="btn-content">{{ t('login.log_in_using') }} {{ biometricTypeAvailable }}</span>
                    </button>
                  </div>
                  <div v-if="showMsftLogin && step === 2" class="my-3">
                    <button id="btn-msft-login" type="button" @click="loginViaProvider(msftLoginUrl)" class="btn btn--with-icon btn--msft">
                      <span class="btn-icon"></span>
                      <span class="btn-content">{{ t('login.log_in_using_msft') }}</span>
                    </button>
                  </div>
                  <div v-if="allowedBankID() && step !== 3" class="my-3">
                    <button id="btn-bankid-login" type="button" @click="authenticateWithBankID()" class="btn btn--with-icon btn--bank-id">
                      <span class="btn-icon"></span>
                      <span class="btn-content">{{ t('login.log_in_using_bankid') }}</span>
                    </button>
                  </div>
                </div>
                <div v-if="step !== 3" class="form-group form-group--footer-text">
                  <router-link :to="email !== null && email !== '' ? `/forgotten-password?email=${encodeURIComponent(email)}` : '/forgotten-password'">{{ t('login.forgot_password') }}</router-link>
                </div>
                <div v-if="step === 3" class="form-group form-group--footer-text">
                  <a class="text-decoration-underline cursor-pointer" @click="resetForm">{{ t('login.back_to_login') }}</a>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
    <Footer/>
  </div>
</template>

<style scoped>
.mdi.mdi-large-icon {
  font-size: 8em;
}

.maintenance-title {
  font-size: 3em;
  font-weight: 600;
  color: var(--primaryColor);
}

.maintenance-description {
  font-size: 1.5em;
}
</style>
